import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import { ParametersContext } from 'components/ParametersGuard';
import ClaimsTable from 'components/claims/ClaimsPage/ClaimsTable';
import Modal from 'components/shared/Modal/Modal';
import { Claim } from 'store/types/Claim';
import ClaimsTableType from 'store/enums/ClaimsTableType';
import ClaimUploadType from 'store/enums/ClaimUploadType';
import ApplicationType from 'store/types/ApplicationType';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';

import styles from 'components/claims/ClaimsPage/ClaimsPage.module.scss';

interface Props {
  myClaims?: Claim[];
  claimsAgainstMe?: Claim[];
  applicationTypes?: ApplicationType[];
  loading?: boolean;
  onApplicationCreateClick: (applicationId: string) => void;
  onApplicationDeleteClick?: (claim: Claim) => void;
  onDisputeClaimClick?: (claim: Claim) => void;
  onCloseClaimClick?: (claim: Claim) => void;
  onFileUploadClick?: (claim: Claim, uploadType: ClaimUploadType) => void;
}

const ActiveClaimsTab: React.FunctionComponent<Props> = ({
  myClaims,
  claimsAgainstMe,
  applicationTypes,
  loading,
  onApplicationCreateClick,
  onApplicationDeleteClick,
  onDisputeClaimClick,
  onCloseClaimClick,
  onFileUploadClick,
}) => {
  const [createClaimModalOpen, setCreateClaimModalOpen] = useState(false);
  const [createDisputeModalOpen, setCreateDisputeModalOpen] = useState(false);

  const {
    claims: { claimsApplicationTypeId, disputesApplicationTypeId, myClaimsTableHeader, claimsAgainstMeTableHeader },
  } = useContext(ParametersContext);

  const claimDescription = useMemo(() => {
    if (!claimsApplicationTypeId) return '';
    const claimAppType = applicationTypes?.find((appType) => appType.id === claimsApplicationTypeId);
    return claimAppType?.description || '';
  }, [applicationTypes, claimsApplicationTypeId]);

  const disputeDescription = useMemo(() => {
    if (!disputesApplicationTypeId) return '';
    const disputeAppType = applicationTypes?.find((appType) => appType.id === disputesApplicationTypeId);
    return disputeAppType?.description || '';
  }, [applicationTypes, disputesApplicationTypeId]);

  const handleCreateClaimModalClose = () => {
    setCreateClaimModalOpen(false);
  };

  const handleCreateDisputeModalClose = () => {
    setCreateDisputeModalOpen(false);
  };

  const handleCreateClaimSubmit = useCallback(() => {
    setCreateClaimModalOpen(false);
    onApplicationCreateClick(claimsApplicationTypeId ?? '');
  }, [claimsApplicationTypeId, onApplicationCreateClick]);

  const handleCreateDisputeSubmit = useCallback(() => {
    setCreateDisputeModalOpen(false);
    onApplicationCreateClick(disputesApplicationTypeId ?? '');
  }, [disputesApplicationTypeId, onApplicationCreateClick]);

  return (
    <>
      <Grid {...defaultGridContainerProps}>
        <Grid {...defaultGridItemProps}>
          <Box sx={{ display: 'flex', gap: '16px' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => setCreateClaimModalOpen(true)}
            >
              Gold Medallion Claim Form
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: '16px', mt: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              onClick={() => setCreateDisputeModalOpen(true)}
            >
              Dispute Resolution Process Form
            </Button>
          </Box>
        </Grid>
        <Grid {...defaultGridItemProps}>
          <Typography variant="h2" className={styles.tableTitle}>
            {'Filed'}
          </Typography>
          {myClaimsTableHeader && <p dangerouslySetInnerHTML={{ __html: myClaimsTableHeader }} />}
          <ClaimsTable
            tableType={ClaimsTableType.MyClaims}
            data={myClaims}
            loading={loading}
            onApplicationDeleteClick={onApplicationDeleteClick}
            onDisputeClaimClick={onDisputeClaimClick}
            onCloseClaimClick={onCloseClaimClick}
            onFileUploadClick={onFileUploadClick}
          />
        </Grid>
        <Grid {...defaultGridItemProps}>
          <Typography variant="h2" className={styles.tableTitle}>
            {'Filed Against Me'}
          </Typography>
          {claimsAgainstMeTableHeader && <p dangerouslySetInnerHTML={{ __html: claimsAgainstMeTableHeader }} />}
          <ClaimsTable
            tableType={ClaimsTableType.ClaimsAgainstMe}
            data={claimsAgainstMe}
            loading={loading}
            onDisputeClaimClick={onDisputeClaimClick}
            onFileUploadClick={onFileUploadClick}
          />
        </Grid>
      </Grid>
      <Modal
        title={'Gold Medallion Claim Form'}
        loading={loading}
        open={createClaimModalOpen}
        onClose={handleCreateClaimModalClose}
        maxWidth={'sm'}
        actions={
          <>
            <Button color={'secondary'} variant={'outlined'} onClick={handleCreateClaimModalClose}>
              {'Cancel'}
            </Button>
            <Button type={'submit'} variant={'contained'} onClick={handleCreateClaimSubmit}>
              {'Continue to Form'}
            </Button>
          </>
        }
      >
        <Typography variant="body1" dangerouslySetInnerHTML={{ __html: claimDescription }} />
      </Modal>
      <Modal
        title={'Dispute Resolution Process Form'}
        loading={loading}
        onClose={handleCreateDisputeModalClose}
        open={createDisputeModalOpen}
        maxWidth={'sm'}
        actions={
          <>
            <Button color={'secondary'} variant={'outlined'} onClick={handleCreateDisputeModalClose}>
              {'Cancel'}
            </Button>
            <Button type={'submit'} variant={'contained'} onClick={handleCreateDisputeSubmit}>
              {'Continue to Form'}
            </Button>
          </>
        }
      >
        <Typography variant="body1" dangerouslySetInnerHTML={{ __html: disputeDescription }} />
      </Modal>
    </>
  );
};

export default ActiveClaimsTab;
